// What the Devices page says about the account key and the signed device list: whose key it is,
// its fingerprint, and which devices the list names. The list counts only once it verifies
// against the account key, so the API cannot claim a device reads conversations when it does not.
import { queryOptions } from "@tanstack/react-query";

import { base64ToBytes } from "../proto/bytes";
import type { SigAlg } from "../proto/frames";
import type { Proto } from "../proto/index";
import { bytesToUlid } from "../proto/ulid";
import type { AccountKeyMode } from "./api";
import { accountFingerprint, groupFingerprint } from "./key";
import { accountRole, type AccountRole, type AccountStatus, loadAccountStatus } from "./setup";

/** Shared by the account key panel and the devices table, refreshed after every change. */
export const accountStatusQuery = () => queryOptions({ queryKey: ["account-key-status"], queryFn: loadAccountStatus });

export interface AccountSummary {
  role: AccountRole;
  /** Grouped in fours; absent while no account key exists. */
  fingerprint?: string;
  /** The device ids the verified list names (not the escrow); absent when it cannot be verified. */
  recipients?: string[];
  version?: number;
  /** Who holds the private half; absent while no account key exists. */
  mode?: AccountKeyMode;
}

/** The platform's escrow entry of a managed list (D59): a recipient, not a device. */
const ESCROW_KIND = "escrow";

/** The device ids a stored list names, if it verifies against the account key. */
export function verifiedRecipients(proto: Proto, status: AccountStatus): string[] | undefined {
  const { list } = status;
  const key = status.server
    ? { alg: status.server.alg as SigAlg, bytes: base64ToBytes(status.server.public_key) }
    : status.local && { alg: status.local.alg, bytes: status.local.publicKey };
  if (!list || !key) return undefined;
  try {
    const verified = proto.verifyRecipientList(key, {
      document: base64ToBytes(list.document),
      signedAt: list.signed_at,
      signature: { alg: list.sig_alg as SigAlg, bytes: base64ToBytes(list.signature) },
    });
    return verified.recipients.filter((r) => (r.kind as string) !== ESCROW_KIND).map((r) => bytesToUlid(r.device_id));
  } catch {
    return undefined;
  }
}

export function accountSummary(status: AccountStatus, proto: Proto | undefined): AccountSummary {
  const role = accountRole(status);
  const raw = role === "holder" && status.local && proto ? accountFingerprint(proto, status.local.publicKey) : status.server?.fingerprint;
  return {
    role,
    fingerprint: raw ? groupFingerprint(raw) : undefined,
    recipients: proto ? verifiedRecipients(proto, status) : undefined,
    version: status.list?.version,
    mode: status.server?.mode as AccountKeyMode | undefined,
  };
}
